import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Bot, RotateCcw, X, ExternalLink, CheckCircle2 } from "lucide-react";
import { Kicker } from "@/components/ui/kicker";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { SeverityDot } from "@/components/ui/severity-dot";
import { Timestamp } from "@/components/ui/timestamp";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { useCanEdit } from "@/lib/project-access";
import { cn } from "@/lib/utils";

interface AgentWorkQueueCardProps {
  projectId: string;
  className?: string;
}

export function AgentWorkQueueCard({
  projectId,
  className,
}: AgentWorkQueueCardProps) {
  const canEdit = useCanEdit();
  const { rows, isLoading, cancel, releaseLease, isPending } =
    useWorkRequests(projectId);

  const claimedCount = useMemo(
    () => rows.filter((r) => r.status === "claimed").length,
    [rows],
  );
  const queuedCount = useMemo(
    () => rows.filter((r) => r.status === "queued").length,
    [rows],
  );

  return (
    <section
      aria-labelledby="agent-queue-heading"
      className={cn(
        "rounded-lg border border-border/80 bg-card p-5 space-y-4 shadow-xs",
        className,
      )}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Kicker as="h2">
            <span id="agent-queue-heading">Agent work queue</span>
          </Kicker>
        </div>

        <div className="flex items-center gap-2 text-xs">
          {claimedCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-mark-helps/15 px-2.5 py-0.5 font-medium text-mark-helps">
              <span className="h-1.5 w-1.5 rounded-full bg-mark-helps animate-pulse" />
              {claimedCount} in agent session
            </span>
          ) : queuedCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-0.5 font-medium text-amber-600 dark:text-amber-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {queuedCount} queued for agent
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-0.5 font-medium text-muted-foreground">
              <CheckCircle2 className="h-3 w-3 text-mark-helps" />
              Queue idle
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="space-y-2 py-2">
          <div className="h-10 rounded bg-muted/40 animate-pulse" />
          <div className="h-10 rounded bg-muted/40 animate-pulse" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-2">
          <p className="text-xs text-muted-foreground">
            No active agent tasks. Queue fixes from a finding to make work
            available for local developers and agents to pull.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((req) => {
            const isClaimed = req.status === "claimed";
            const elapsedMinutes =
              isClaimed && req.claimedAt
                ? Math.max(
                    0,
                    Math.floor(
                      (Date.now() - new Date(req.claimedAt).getTime()) / 60000,
                    ),
                  )
                : 0;
            const isStalled = isClaimed && elapsedMinutes >= 45;
            const targetTitle =
              req.canonicalTitle ?? req.scenarioName ?? `${req.kind} task`;
            const findingLink = req.canonicalId
              ? `/projects/${projectId}/findings/${req.canonicalId}`
              : req.scenarioId
                ? `/projects/${projectId}/scenarios/${req.scenarioId}`
                : null;

            return (
              <li
                key={req.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs font-semibold",
                        isClaimed
                          ? "bg-mark-helps/15 text-mark-helps"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {isClaimed ? (
                        <>
                          <span className="h-1 w-1 rounded-full bg-mark-helps animate-pulse" />
                          Running
                        </>
                      ) : (
                        "Queued"
                      )}
                    </span>

                    <span className="inline-flex items-center rounded border border-border px-1.5 py-0.5 text-2xs font-medium text-muted-foreground capitalize">
                      {req.kind}
                    </span>

                    {req.findingSeverity && (
                      <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground">
                        <SeverityDot
                          tone={
                            req.findingSeverity === "critical"
                              ? "critical"
                              : req.findingSeverity === "major"
                                ? "important"
                                : "minor"
                          }
                        />
                        {req.findingSeverity}
                      </span>
                    )}

                    {findingLink ? (
                      <Link
                        to={findingLink}
                        className="text-sm font-medium hover:underline inline-flex items-center gap-1 text-foreground truncate max-w-sm"
                      >
                        {targetTitle}
                        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </Link>
                    ) : (
                      <span className="text-sm font-medium text-foreground truncate max-w-sm">
                        {targetTitle}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {isClaimed ? (
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <span>
                          Claimed by{" "}
                          <span className="font-medium text-foreground">
                            {req.claimedBy ?? "Agent session"}
                          </span>
                          {req.claimedAt && (
                            <>
                              {" "}
                              · <Timestamp date={req.claimedAt} />
                            </>
                          )}
                        </span>
                        {isStalled && (
                          <span className="inline-flex items-center rounded bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 text-2xs font-semibold text-amber-600 dark:text-amber-400">
                            Held {elapsedMinutes}m (stalled?)
                          </span>
                        )}
                      </span>
                    ) : (
                      <span>
                        Requested by {req.requestedBy} ·{" "}
                        <Timestamp date={req.requestedAt} />
                      </span>
                    )}

                    {req.assigneeName && (
                      <span className="text-muted-foreground">
                        (Assigned to {req.assigneeName})
                      </span>
                    )}
                  </p>
                </div>

                {canEdit && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isClaimed && (
                      <SimpleTooltip content="Release lease back to queue if the agent disconnected or stalled">
                        <Button
                          variant={isStalled ? "outline" : "ghost"}
                          size="sm"
                          className={cn(
                            "h-7 text-xs gap-1 px-2",
                            isStalled
                              ? "border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 font-semibold"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          disabled={isPending}
                          onClick={() => releaseLease(req.id)}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Release
                        </Button>
                      </SimpleTooltip>
                    )}

                    <SimpleTooltip content="Cancel and remove this request from the agent queue">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        disabled={isPending}
                        onClick={() => cancel(req.id)}
                        aria-label="Cancel agent request"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </SimpleTooltip>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
